import mime from 'mime';
import { Accept, DropzoneOptions, ErrorCode, FileRejection } from 'react-dropzone';
import { I18nextProviderProps } from 'react-i18next';

import { UploadingStateEnum } from '~modules/attachedFiles/types/UploadingStateEnum';
import { FileFormatModel } from '~modules/fileTypes/models/FileFormatModel';

export default class FileHelper {
  static createFileFormData = (file: File): FormData => {
    const formData = new FormData();

    formData.append('file', file);

    return formData;
  };

  static createFormData(data: { [key: string]: any }): FormData {
    const formData = new FormData();

    for (const key in data) {
      if (Object.hasOwnProperty.call(data, key)) {
        const value = data[key];

        if (value !== '' && value !== null && value !== undefined) {
          if (Array.isArray(value)) {
            value.forEach((val: any) => {
              formData.append(`${key}[]`, val);
            });
          } else {
            formData.append(key, value);
          }
        }
      }
    }

    return formData;
  }

  /**
   * Генерирует объект Accept для атрибута accept в input[type="file"]
   * Используется для фильтрации файлов в диалоге выбора файла
   *
   * @param fileFormats - разрешенные форматы, получаем от API
   * @returns Accept - объект с MIME-типами и расширениями файлов
   */
  static generateAcceptFormats = (fileFormats: FileFormatModel[]): Accept => {
    const mimeMap: Accept = {};

    fileFormats.forEach(({ fileExt }) => {
      const mimeType = mime.getType(fileExt);

      if (mimeType) {
        if (mimeMap[mimeType]) {
          mimeMap[mimeType].push(`.${fileExt}`);
        } else {
          mimeMap[mimeType] = [`.${fileExt}`];
        }
      }
    });

    return mimeMap;
  };

  /**
   * Получение строки из числа с плавающей точкой с заменой точки на запятую.
   *
   * @param fileFormats - разрешенные форматы, получаем от API
   * @param t - экземпляр t из i18n
   * @returns DropzoneOptions["validator"]
   */
  static generateFileDropzoneAcceptFormats = (
    fileFormats: FileFormatModel[],
    t: I18nextProviderProps['i18n']['t'],
  ): DropzoneOptions['validator'] => {
    const mimeMap = this.generateAcceptFormats(fileFormats);

    return (file: File) => {
      const extension = file.name?.split('.').pop()?.toLowerCase();
      const mimeType = mime.getType(extension!);
      const acceptFormats = Object.values(mimeMap)
        .flat()
        .map((ext) => ext.slice(1)) // удаляем точку
        .join(', ');

      // Проверяем, что MIME-тип существует и конкретное расширение разрешено
      if (!mimeType || !mimeMap[mimeType] || !mimeMap[mimeType].includes(`.${extension!}`)) {
        return {
          code: ErrorCode.FileInvalidType,
          message: t(`error.file.${ErrorCode.FileInvalidType}`, { acceptFormats, ns: 'common' }),
        };
      }

      const maxSize = fileFormats.find((ext) => ext.fileExt === extension)?.maxSizeByte || 0;

      const maxSizeMB = (maxSize / 1024 / 1024).toFixed(2);

      if (file.size > maxSize) {
        return {
          code: ErrorCode.FileTooLarge,
          message: t(`error.file.${ErrorCode.FileTooLarge}`, { size: maxSizeMB, ns: 'common' }),
        };
      }

      return null;
    };
  };

  static downloadFileByURL = async (url: string, fileName?: string) => {
    const response = await fetch(url);
    const blob = await response.blob();
    const urlBlob = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = urlBlob;
    link.download = fileName ?? '';
    link.click();
    link.remove();

    URL.revokeObjectURL(urlBlob);
  };

  /**
   *
   * Получение имени файла по его пути.
   * @param path - путь к файлу, например /public/assets/images/icons/menu/q_traffic.svg
   */
  static getFileName = (path: string) => {
    const regex = /\/([^/]+)\.[^/]+$/;
    const match = path.match(regex);

    if (match && match[1]) {
      return match[1];
    }

    return '';
  };

  /**
   *
   * Получение мапы в виде { file_name: file_path } из списка файлов, полученных через import.meta.glob.
   * Модули заранее не импортируются, импорт происходит при использовании файла.
   */
  static getFilesMap = (svgList: Record<string, unknown>) => {
    return Object.keys(svgList).reduce((acc: Record<string, string>, curr) => {
      acc = {
        ...acc,
        [this.getFileName(curr)]: curr.replace(/^\/public/, ''),
      };

      return acc;
    }, {});
  };

  /**
   *
   * Создаем модели файлов после начала отправки на бэк для отображения UI с лоадером
   */
  static createUploadedFiles = (files: File[]) => {
    let counter = Date.now();
    return files.map((file) => ({
      fileId: file.lastModified + counter++,
      uploadingState: UploadingStateEnum.LOADING,
      originalUrl: '',
      previewUrl: '',
    }));
  };

  /**
   * Создает Map с уникальными идентификаторами для файлов
   * Используется для сопоставления файлов с их временными ID при загрузке
   * @param files - массив файлов
   * @returns Map<File, number> - маппинг файла на его уникальный идентификатор
   */
  static createFileIdMap = (files: File[]): Map<File, number> => {
    let counter = Date.now();
    const fileIdMap = new Map<File, number>();
    files.forEach((file) => {
      fileIdMap.set(file, file.lastModified + counter++);
    });
    return fileIdMap;
  };

  /**
   * Обрабатывает ошибки файлов и возвращает локализованные сообщения
   */
  static processFileErrors = (
    fileRejections: FileRejection[],
    t: I18nextProviderProps['i18n']['t'],
    maxFiles?: number,
  ): string[] => {
    const errorMessages = new Set<string>();

    fileRejections.forEach(({ errors }) => {
      /**
       * Для каждого файла сначала группируем ошибки по коду.
       * Это позволяет:
       * - переопределять "стандартные" сообщения react-dropzone нашими кастомными
       *   (валидатор вызывается после внутренних проверок, поэтому его сообщение
       *   перезаписывает сообщение с тем же кодом, например `file-invalid-type`);
       * - не дублировать одинаковые ошибки для одного файла.
       */
      const errorsByCode = new Map<ErrorCode | string, string>();

      errors.forEach((error) => {
        if (error.code === ErrorCode.TooManyFiles) {
          // Для TooManyFiles всегда используем локализованное сообщение с учётом лимита
          errorsByCode.set(
            ErrorCode.TooManyFiles,
            t('input.fileDropzone.validation.countLimit', { count: maxFiles || 1 }),
          );
          return;
        }

        // Для остальных ошибок (включая FileInvalidType и FileTooLarge)
        // сохраняем/перезаписываем сообщение по коду ошибки.
        // Таким образом, если сначала пришла нативная ошибка от `accept`/`maxSize`,
        // а затем валидатор вернул ошибку с тем же кодом, останется только наше сообщение.
        errorsByCode.set(error.code ?? error.message, error.message);
      });

      errorsByCode.forEach((message) => {
        errorMessages.add(message);
      });
    });

    return Array.from(errorMessages);
  };
}
